import {
  DEV_PERSONAS,
  type DevPersonaId,
  HEADERS,
  ROLE_LABELS,
  type WebRuntimeConfig,
} from '@bnb/contracts';
import { BUILD_VERSION } from './build-version.ts';
import { type KeyValueStorage, safeLocalStorage } from './utils.ts';

/**
 * Sign-in, abstracted over two modes chosen by /config.json:
 *  - 'dev-personas' (laptop only): pick Owner / Office / Barn crew / Viewer; every API call
 *    carries `x-bnb-dev-persona`. The API accepts that header ONLY when STAGE=local.
 *  - 'cognito' (AWS): passwordless email code or passkey via aws-amplify (lib/auth-cognito.ts,
 *    loaded on demand so laptop builds never download Amplify); calls carry
 *    `Authorization: Bearer <access token>`.
 * Screens never branch on the mode except the sign-in page itself.
 */

export type AuthMode = WebRuntimeConfig['auth']['mode'];

/** Who the browser thinks is signed in. The API's `me` query is the authority on role. */
export interface AuthUser {
  label: string;
  email: string | null;
  personaId?: DevPersonaId;
}

interface AuthBase {
  /** null = signed out. Cheap; safe to call in route guards. */
  getUser(): Promise<AuthUser | null>;
  /** Headers for every API request (build version + credentials). */
  getRequestHeaders(): Promise<Record<string, string>>;
  signOut(): Promise<void>;
  /** Notified after sign-in/sign-out (router guards re-run, caches clear). */
  subscribe(listener: () => void): () => void;
}

export interface DevPersonaAuth extends AuthBase {
  mode: 'dev-personas';
  signInAs(id: DevPersonaId): void;
  currentPersona(): DevPersonaId | null;
}

/** Result of starting an email sign-in. */
export type EmailSignInStep = 'code-sent' | 'signed-in';

export interface CognitoAuth extends AuthBase {
  mode: 'cognito';
  /** Sends a one-time code to the email address (Cognito EMAIL_OTP). */
  startEmailSignIn(email: string): Promise<EmailSignInStep>;
  confirmEmailCode(code: string): Promise<void>;
  /** Passkey (WebAuthn) sign-in for users who registered one. */
  signInWithPasskey(email: string): Promise<void>;
}

export type AuthClient = DevPersonaAuth | CognitoAuth;

export const DEV_PERSONA_STORAGE_KEY = 'bnb.devPersona';

export function isDevPersonaId(value: unknown): value is DevPersonaId {
  return typeof value === 'string' && DEV_PERSONAS.some((p) => p.id === value);
}

export interface RequestHeaderInput {
  mode: AuthMode;
  build: string;
  personaId?: string | null;
  accessToken?: string | null;
}

/**
 * The ONE place request credentials are decided (unit-tested in test/auth.test.ts):
 *  - always `x-bnb-build`
 *  - dev-personas → `x-bnb-dev-persona`, only for a known persona id
 *  - cognito → `Authorization: Bearer …`, never the persona header (even if one is stored)
 */
export function buildRequestHeaders(input: RequestHeaderInput): Record<string, string> {
  const headers: Record<string, string> = { [HEADERS.clientBuild]: input.build };
  if (input.mode === 'dev-personas') {
    if (isDevPersonaId(input.personaId)) headers[HEADERS.devPersona] = input.personaId;
  } else if (input.accessToken) {
    headers.authorization = `Bearer ${input.accessToken}`;
  }
  return headers;
}

/** A tiny listener set used by both auth implementations. */
export function createEmitter() {
  const listeners = new Set<() => void>();
  return {
    emit() {
      for (const l of listeners) l();
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export function createDevPersonaAuth(
  storage: KeyValueStorage = safeLocalStorage,
  build: string = BUILD_VERSION,
): DevPersonaAuth {
  const emitter = createEmitter();
  const current = (): DevPersonaId | null => {
    const stored = storage.getItem(DEV_PERSONA_STORAGE_KEY);
    return isDevPersonaId(stored) ? stored : null;
  };

  return {
    mode: 'dev-personas',
    currentPersona: current,
    signInAs(id) {
      storage.setItem(DEV_PERSONA_STORAGE_KEY, id);
      emitter.emit();
    },
    async getUser() {
      const id = current();
      const persona = DEV_PERSONAS.find((p) => p.id === id);
      if (!persona) return null;
      return {
        label: `${persona.name} · ${ROLE_LABELS[persona.role]}`,
        email: persona.email,
        personaId: persona.id,
      };
    },
    async getRequestHeaders() {
      return buildRequestHeaders({ mode: 'dev-personas', build, personaId: current() });
    },
    async signOut() {
      storage.removeItem(DEV_PERSONA_STORAGE_KEY);
      emitter.emit();
    },
    subscribe: emitter.subscribe,
  };
}

/** Build the auth client for this stage. Cognito code is only downloaded when needed. */
export async function createAuthClient(config: WebRuntimeConfig): Promise<AuthClient> {
  if (config.auth.mode === 'dev-personas') return createDevPersonaAuth();
  const { createCognitoAuth } = await import('./auth-cognito.ts');
  return createCognitoAuth(config);
}
